import { useCallback, useMemo, useState, type ReactNode } from 'react'
import { SelectAccountsPanel } from 'screens/BrandKit/LinkedAccountsDrawer/SelectAccountsPanel'
import { Panel } from './consts'
import { ManageAccountsPanel } from 'screens/BrandKit/LinkedAccountsDrawer/ManageAccountsPanel'
import { useCreateBrandKitSource } from 'hooks/queries/useCreateBrandKitSource'
import { ExternalIntegration, ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { IntegrationAuth } from 'api/clients/IntegrationAuthsClient'
import { ConnectProviderPanel } from 'screens/BrandKit/LinkedAccountsDrawer/ConnectProviderPanel'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { pluralize } from 'utils'

export function useLinkedAccountsController({
  brandKitId,
  initialPanel = Panel.SelectAccounts,
  onExit,
}: {
  brandKitId: number
  initialPanel?: Panel
  onExit: () => void
}) {
  const [provider, setProvider] = useState<
    ExternalIntegrationEnum.Facebook | ExternalIntegrationEnum.Instagram | ExternalIntegrationEnum.LinkedIn | undefined
  >(undefined)
  const [integrationAuth, setIntegrationAuth] = useState<IntegrationAuth | undefined>(undefined)
  const [panel, setPanel] = useState(initialPanel)
  const [selectAccountsEnteredFromManage, setSelectAccountsEnteredFromManage] = useState(false)
  const { mutateAsync: createBrandKitSource } = useCreateBrandKitSource({ mutationKey: 'createBrandKitSource' })

  const handleLink = useCallback(
    (selectedAccounts: ExternalIntegration[]) => {
      Promise.allSettled(
        selectedAccounts.map((integration) => {
          return createBrandKitSource({
            brandKitId,
            source: { type: 'ExternalIntegration', id: integration.id },
          })
        }),
      ).then((results) => {
        const successes = results.filter((result) => result.status === 'fulfilled').length
        if (successes > 0) {
          showToastMessage(`${successes} ${pluralize('Account', successes)} added to your Brand Kit!`)
        }
      })
      setIntegrationAuth(undefined)
      setSelectAccountsEnteredFromManage(false)
      setPanel(Panel.ManageAccounts)
    },
    [brandKitId, createBrandKitSource],
  )

  const currentPanel = useMemo(() => {
    if (provider) return Panel.ConnectProvider

    return panel
  }, [provider, panel])

  const handleBack = useCallback(() => {
    if (currentPanel === Panel.ConnectProvider) {
      setProvider(undefined)
      setPanel(Panel.SelectAccounts)
      return
    }

    if (currentPanel === Panel.SelectAccounts && selectAccountsEnteredFromManage) {
      setSelectAccountsEnteredFromManage(false)
      setPanel(Panel.ManageAccounts)
      return
    }

    onExit()
  }, [currentPanel, onExit, selectAccountsEnteredFromManage])

  const heading = useMemo(() => {
    if (currentPanel === Panel.ManageAccounts) {
      return 'Linked Accounts'
    }

    if (currentPanel === Panel.SelectAccounts) {
      return 'Connect Accounts'
    }

    if (provider === ExternalIntegrationEnum.LinkedIn) {
      return 'Connect LinkedIn Account'
    }

    return 'Connect Meta Account'
  }, [currentPanel, provider])

  const panels: ReactNode = (
    <>
      {currentPanel === Panel.ManageAccounts && (
        <ManageAccountsPanel
          brandKitId={brandKitId}
          onLinkMoreAccounts={() => {
            setSelectAccountsEnteredFromManage(true)
            setPanel(Panel.SelectAccounts)
          }}
          onNavigateToSelectAfterLastRemoved={() => {
            setSelectAccountsEnteredFromManage(false)
            setPanel(Panel.SelectAccounts)
          }}
        />
      )}
      {currentPanel === Panel.SelectAccounts && (
        <SelectAccountsPanel
          brandKitId={brandKitId}
          onLink={handleLink}
          integrationAuth={integrationAuth}
          onSelectFacebook={() => setProvider(ExternalIntegrationEnum.Facebook)}
          onSelectInstagram={() => setProvider(ExternalIntegrationEnum.Instagram)}
          onSelectLinkedIn={() => setProvider(ExternalIntegrationEnum.LinkedIn)}
        />
      )}
      {currentPanel === Panel.ConnectProvider && !!provider && (
        <ConnectProviderPanel
          onIntegrationAuthCreated={(auth) => {
            setIntegrationAuth(auth)
            setProvider(undefined)
            setPanel(Panel.SelectAccounts)
          }}
          provider={provider}
        />
      )}
    </>
  )

  return { heading, handleBack, panels }
}
